import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout2 from './components/Layout2/Layout2.jsx';
import OnboardingModal from './components/OnboardingModal';
import Seo from './seo/Seo';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import VerifyEmailPage from './pages/Auth/VerifyEmailPage';
import NewLandingPage from './pages/NewLandingPage';
import Store2 from './pages/Store2';
import Dashboard2 from './pages/Dashboard2';
import Support2 from './pages/Support2';
import Tests2 from './pages/Tests2';
import Settings2 from './pages/Settings2';
import Subject2 from './pages/Subject2';
import Exam2 from './pages/Exam2';
import Results2 from './pages/Results2';
import Review2 from './pages/Review2';
import CourseDetail from './pages/CourseDetail';
import ExamCreate from './pages/ExamCreate';
import Login from './pages/Login';
import Register from './pages/Register';
import GoogleCallback from './pages/GoogleCallback';
import MICGame from './pages/Games/MICGame';
import MICResults from './pages/Games/MICResults';
import SKTGame from './pages/Games/SKTGame';
import MEKGame from './pages/Games/MEKGame';
import RMSGame from './pages/Games/RMSGame';
import ROTGame from './pages/Games/ROTGame';
import OWTGame from './pages/Games/OWTGame';
import PPTGame from './pages/Games/PPTGame';
import PPTGameResults from './pages/Games/PPTGameResults';
import MICSetup2 from './pages/Games2/MICSetup2';
import SKTSetup2 from './pages/Games2/SKTSetup2';
import MEKSetup2 from './pages/Games2/MEKSetup2';
import RMSSetup2 from './pages/Games2/RMSSetup2';
import ROTSetup2 from './pages/Games2/ROTSetup2';
import OWTSetup2 from './pages/Games2/OWTSetup2';
import PPTSetup2 from './pages/Games2/PPTSetup2';
import Coupon from './pages/Coupon';
import QuestionLookup from './pages/QuestionLookup';
import AdminPanel from './pages/AdminPanel';
import BroadcastModal from './components/BroadcastModal';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import TermsOfService from './pages/Legal/TermsOfService';
import PrivacyPolicyPage from './pages/Legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/Legal/TermsOfServicePage';
import RefundPolicyPage from './pages/Legal/RefundPolicyPage';
import Pricing from './pages/Pricing';

// Admin console (served on admin.air-brief.com). Lazy-loaded so its bundle
// never ships to regular students. Gated STRICTLY on the hostname's first
// label being "admin" (e.g. admin.air-brief.com, admin.localhost) — never on
// the main www/apex domain, and with no persistent override.
const AdminApp = lazy(() => import('./admin/AdminApp'));
const isAdminHost = (() => {
  try {
    // Clean up any stale dev override from earlier builds.
    localStorage.removeItem('force_admin');
    return window.location.hostname.split('.')[0] === 'admin';
  } catch { return false; }
})();

// Redirect keeping the query string (e.g. /profile-settings?tab=payments)
const KeepQuery = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

// /store2 -> /store, /exam2/123/results -> /exam/123/results
const Unsuffix = () => {
  const { pathname, search } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/([a-z]+)2(?=\/|$)/, '/$1')}${search}`} replace />;
};

function AppRoutes() {
  const { isAuthenticated, loading, user } = useAuth();

  // Admin subdomain → render the admin console instead of the student app.
  if (isAdminHost) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-gray-900" />}>
        <AdminApp />
      </Suspense>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        {/* Public pages - accessible to everyone */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/newlanding" element={<Navigate to="/" replace />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:uid/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:uid/:token" element={<VerifyEmailPage />} />
        
        {!isAuthenticated ? (
          <>
            <Route path="/" element={<NewLandingPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/question" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        ) : (
          <>
            <Route element={<Layout2 />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard2 />} />
              <Route path="/store" element={<Store2 />} />
              <Route path="/support" element={<Support2 />} />
              <Route path="/tests" element={<Tests2 />} />
              <Route path="/settings" element={<Settings2 />} />
              <Route path="/courses/:courseId" element={<CourseDetail />} />
              <Route path="/subjects/:subjectId" element={<Subject2 />} />
              <Route path="/exam/create" element={<ExamCreate />} />
              <Route path="/exam/:examId" element={<Exam2 />} />
              <Route path="/exam/:examId/results" element={<Results2 />} />
              <Route path="/exam/:examId/review" element={<Review2 />} />

              <Route path="/games/mic/:subjectId" element={<MICSetup2 />} />
              <Route path="/games/mic/:subjectId/play" element={<MICGame />} />
              <Route path="/games/mic/:subjectId/results" element={<MICResults />} />
              <Route path="/games/skt/:subjectId" element={<SKTSetup2 />} />
              <Route path="/games/skt/:subjectId/play" element={<SKTGame />} />
              <Route path="/games/mek/:subjectId" element={<MEKSetup2 />} />
              <Route path="/games/mek/:subjectId/play" element={<MEKGame />} />
              <Route path="/games/rms/:subjectId" element={<RMSSetup2 />} />
              <Route path="/games/rms/:subjectId/play" element={<RMSGame />} />
              <Route path="/games/rot/:subjectId" element={<ROTSetup2 />} />
              <Route path="/games/rot/:subjectId/play" element={<ROTGame />} />
              <Route path="/games/owt/:subjectId" element={<OWTSetup2 />} />
              <Route path="/games/owt/:subjectId/play" element={<OWTGame />} />
              <Route path="/games/ppt/:subjectId" element={<PPTSetup2 />} />
              <Route path="/games/ppt/:subjectId/play" element={<PPTGame />} />
              <Route path="/games/ppt/:subjectId/results" element={<PPTGameResults />} />

              <Route path="/admin/stats" element={<AdminPanel />} />
              <Route path="/admin/questions" element={<QuestionLookup />} />
              <Route path="/admin/coupons" element={<Coupon />} />
            </Route>

            {/* Old addresses (emails, bookmarks, the redesign preview) */}
            <Route path="/profile-settings" element={<KeepQuery to="/settings" />} />
            {['dashboard2', 'store2', 'support2', 'tests2', 'settings2', 'subjects2/*', 'exam2/*', 'games2/*'].map((path) => (
              <Route key={path} path={`/${path}`} element={<Unsuffix />} />
            ))}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </>
        )}
      </Routes>
      
      {/* Broadcast Messages */}
      {isAuthenticated && <BroadcastModal />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <Seo noindex={isAdminHost} />
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
